/**
 * To-do inbox + Settings screens of T11-43 … T11-46 (design/04 §2, §13, §15):
 *
 * | route | screen | who |
 * |---|---|---|
 * | `/inbox`, `/inbox/:itemId` | to-do and alerts (T11-43) | `inbox.read` |
 * | `/settings/notifications` (`?tab=people|emails|sent`), `/settings/notifications/log/:id` | who is told, e-mail preview, sent e-mails (T11-43) | `notifications.read` (changes `settings.write`) |
 * | `/settings/store` | shop details + 特商法 progress (T11-44) | `settings.read` (changes `settings.write` + `cms.edit`) |
 * | `/settings/checkout` | "Pause web orders" on the website (T13-41) | `settings.read` (changes `orders.edit`) |
 * | `/settings/staff`, `/settings/staff/:staffId` | staff (T11-45) | `staff.manage` |
 * | `/settings/roles` | roles × permissions (T11-45) | `staff.manage` (changes `staff.roles`) |
 * | `/settings/api-keys` | keys for other apps (T11-45) | `api_keys.manage` |
 * | `/settings/audit-log` | change history (T11-46) | `audit.view` |
 * | `/settings/jobs` | background work, backups, service health (T11-46) | `settings.read` |
 * | `/settings/features` | features switched on later (T11-46) | `settings.read` |
 * | `/settings/website-refresh` | website refresh history + "send again" (T11-46) | `cms.edit` |
 */
import type { ComponentType } from "react";
import type { RouteObject } from "react-router";

import type { RouteHandle } from "../layouts/AppLayout";

const page = (
  path: string,
  handle: RouteHandle,
  load: () => Promise<{ Component: ComponentType }>,
): RouteObject => ({ path, handle, lazy: load });

export const settingsRoutes: readonly RouteObject[] = [
  page(
    "inbox",
    { access: { permission: "inbox.read" }, titleKey: "nav.items.inbox" },
    async () => ({
      Component: (await import("@/features/notifications/InboxPage")).InboxPage,
    }),
  ),
  page(
    "inbox/:itemId",
    { access: { permission: "inbox.read" }, titleKey: "nav.items.inbox" },
    async () => ({ Component: (await import("@/features/notifications/InboxPage")).InboxPage }),
  ),
  page(
    "settings/notifications",
    { access: { permission: "notifications.read" }, titleKey: "nav.items.settings_notifications" },
    async () => ({
      Component: (await import("@/features/notifications/NotificationSettingsPage"))
        .NotificationSettingsPage,
    }),
  ),
  page(
    "settings/notifications/log/:notificationId",
    { access: { permission: "notifications.read" }, titleKey: "nav.items.settings_notifications" },
    async () => ({
      Component: (await import("@/features/notifications/NotificationSettingsPage"))
        .NotificationSettingsPage,
    }),
  ),
  page(
    "settings/store",
    { access: { permission: "settings.read" }, titleKey: "nav.items.settings_store" },
    async () => ({
      Component: (await import("@/features/settings/StoreProfilePage")).StoreProfilePage,
    }),
  ),
  page(
    // T13-41 "Pause web orders" (owner / manager change it; orders.edit)
    "settings/checkout",
    { access: { permission: "settings.read" }, titleKey: "nav.items.settings_checkout" },
    async () => ({
      Component: (await import("@/features/settings/WebOrdersPage")).WebOrdersPage,
    }),
  ),
  page(
    "settings/staff",
    { access: { permission: "staff.manage" }, titleKey: "nav.items.settings_staff" },
    async () => ({ Component: (await import("@/features/settings/StaffPage")).StaffPage }),
  ),
  page(
    "settings/staff/:staffId",
    { access: { permission: "staff.manage" }, titleKey: "nav.items.settings_staff" },
    async () => ({ Component: (await import("@/features/settings/StaffPage")).StaffPage }),
  ),
  page(
    "settings/roles",
    { access: { permission: "staff.manage" }, titleKey: "nav.items.settings_roles" },
    async () => ({
      Component: (await import("@/features/settings/RolesMatrixPage")).RolesMatrixPage,
    }),
  ),
  page(
    "settings/api-keys",
    { access: { permission: "api_keys.manage" }, titleKey: "nav.items.settings_api_keys" },
    async () => ({ Component: (await import("@/features/settings/ApiKeysPage")).ApiKeysPage }),
  ),
  page(
    "settings/audit-log",
    { access: { permission: "audit.view" }, titleKey: "nav.items.settings_audit" },
    async () => ({ Component: (await import("@/features/settings/AuditLogPage")).AuditLogPage }),
  ),
  page(
    "settings/jobs",
    { access: { permission: "settings.read" }, titleKey: "nav.items.settings_jobs" },
    async () => ({ Component: (await import("@/features/settings/JobsPage")).JobsPage }),
  ),
  page(
    "settings/features",
    { access: { permission: "settings.read" }, titleKey: "nav.items.settings_features" },
    async () => ({
      Component: (await import("@/features/settings/FeatureFlagsPage")).FeatureFlagsPage,
    }),
  ),
  page(
    "settings/website-refresh",
    { access: { permission: "cms.edit" }, titleKey: "nav.items.settings_website_refresh" },
    async () => ({
      Component: (await import("@/features/settings/WebsiteRefreshPage")).WebsiteRefreshPage,
    }),
  ),
];

/** Menu addresses served above — they get no "being built" placeholder. */
export const settingsPaths: ReadonlySet<string> = new Set(
  settingsRoutes.map((route) => `/${route.path ?? ""}`).filter((path) => !path.includes(":")),
);
